/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Emitter from '@exc/emitter';
import {Events} from '@exc/shared';
import {gettify} from '../util';
import {messenger} from '../Messenger';
import {Metrics} from '@adobe/exc-app/metrics';

type JSONPrimitive = boolean | Date | null | number | string;
type JSONObject = { [prop: string]: JSONValue };
interface JSONArray extends Array<JSONValue> { [index: number]: JSONValue }
export type JSONValue = JSONPrimitive | JSONObject | JSONArray;

interface APIPromise {
  promise: Promise<any>;
  reject: (value: any) => any;
  resolve: (value: any) => any;
}

interface PendingPromises {
  [key: string]: APIPromise;
}

export interface EventData {
  id?: string;
  type: string;
  value: Record<string, any>;
  changed: string[];
}

type Handler = (value: any, type?: string) => Promise<any>;

export default class BaseApi extends Emitter {
  protected promises: PendingPromises = {};
  protected apiEvents: string[] = [];
  protected trackData: string[] = [];
  protected keyPrefix = '';
  protected event: string = Events.DATA_REQUEST;
  protected defaultType = '';
  protected metrics?: Metrics;
  protected logPrefix = '';

  public get: Handler;
  public set: Handler;
  public delete: Handler;

  public constructor() {
    super();
    messenger.on('message', this.receive.bind(this));
    this.get = this.handler<string, any>('GET');
    this.set = this.handler<string, any>('POST');
    this.delete = this.handler<string, any>('DELETE');
  }

  // https://gist.github.com/gordonbrander/2230317
  /**
   * Generates Unique ID - used to track promises.
   * @returns Unique String Identifier.
   */
  public getUniqueID(): string {
    const id = Math.random().toString(36).substr(2, 9);
    return `${this.keyPrefix}-${id}`;
  }

  /**
   * Sends a request and wait for information.
   * @param {string} event - Event name
   * @param {any} value - Request data
   * @returns Pending promise for the requested value.
   */
  public sendAndReceive(event: string, value: any = {}): Promise<any> {
    const res = {} as APIPromise;
    const id = this.getUniqueID();
    const {method = '', type = ''} = value;
    value.id = id;
    if (this.metrics && this.logPrefix) {
      this.metrics.info(`${this.logPrefix}: Sending ${method} request to Sandbox`, {id, type});
    }
    messenger.send(event, value);
    res.promise = new Promise((resolve, reject): void => {
      res.resolve = resolve;
      res.reject = reject;
    });
    this.promises[id] = res;
    return res.promise;
  }

  /**
   * Default method to handle GET, POST, DELETE.
   * @returns A function that returns a promise.
   */
  protected handler<IN, OUT>(method: string) {
    return (data: IN, type: string = this.defaultType): Promise<OUT> => {
      const key = type || data;
      // Restrict values that can be called
      if (typeof key !== 'string' || !this.apiEvents.includes(key)) {
        return Promise.reject('Requested value is not valid');
      }
      return this.sendAndReceive(this.event, {data, method, type: key});
    };
  }

  /**
   * Method to receive message events, fulfill open promises, and emit events.
   * @param event The event being received.
   */
  public receive(event: MessageEvent): void {
    const {type, value, changed}: EventData = event.data || {};

    switch (type) {
      case Events.CONFIGURE:
        // Make the changed array accessible to SPAs.
        value.changedProperties = changed;
        // If there are changed values, send change events
        changed
          .filter(changeKey => this.apiEvents.includes(changeKey))
          .forEach(changeKey => this.emit(`change:${changeKey}`, value[changeKey]));
        break;
      case Events.DATA_FULFILL: {
        // Resolve all requested promises
        const {id, type: key} = value || {};
        const promise = id && this.promises[id];
        if (id && promise) {
          if (value.rejected) {
            if (this.metrics && this.logPrefix) {
              this.metrics.error(`${this.logPrefix}: Sandbox rejected ${value.method || ''} request`, {id});
            }
            promise.reject(value.error || 'Error making request');
          } else {
            if (this.metrics && this.logPrefix) {
              this.metrics.info(`${this.logPrefix}: Sandbox responded to ${value.method || ''} request`, {id});
            }
            promise.resolve(value.response);
            this.trackData.includes(key) && gettify(value.response, key);
          }
          delete this.promises[id];
        }
        break;
      }
      default:
        return;
    }
  }
}

export const getSingleton = <T extends BaseApi>(apiClass: typeof BaseApi): () => T => {
  let instance: T;
  return function(): T {
    instance || (instance = new apiClass() as T);
    return instance;
  };
};
